export type AnalyticsPropertyValue = boolean | number | string;
export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

const FORBIDDEN_ANALYTICS_PROPERTY_KEYS = new Set([
  'access_code',
  'api_key',
  'authorization',
  'basecamp_name',
  'credential',
  'description',
  'dod_id',
  'edipi',
  'email',
  'file_name',
  'first_name',
  'full_name',
  'guest_name',
  'last_name',
  'name',
  'password',
  'phone',
  'phone_number',
  'query',
  'request_description',
  'room_number',
  'search',
  'secret',
  'staff_name',
  'token',
  'verification_token',
]);

const FORBIDDEN_ANALYTICS_PROPERTY_SUFFIXES = [
  '_access_code',
  '_api_key',
  '_credential',
  '_description',
  '_dod_id',
  '_edipi',
  '_email',
  '_file_name',
  '_first_name',
  '_full_name',
  '_guest_name',
  '_last_name',
  '_name',
  '_password',
  '_phone',
  '_phone_number',
  '_query',
  '_search_query',
  '_room_number',
  '_search',
  '_secret',
  '_staff_name',
  '_token',
  '_verification_token',
];

const normalizeAnalyticsPropertyKey = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

export const isSafeAnalyticsPropertyKey = (key: string): boolean => {
  const normalizedKey = normalizeAnalyticsPropertyKey(key);
  if (FORBIDDEN_ANALYTICS_PROPERTY_KEYS.has(normalizedKey)) return false;

  return !FORBIDDEN_ANALYTICS_PROPERTY_SUFFIXES.some((suffix) =>
    normalizedKey.endsWith(suffix),
  );
};

export const getUnsafeAnalyticsPropertyKeys = (
  properties: Record<string, unknown>,
): string[] =>
  Object.keys(properties).filter((key) => !isSafeAnalyticsPropertyKey(key));

export const filterSafeAnalyticsProperties = (
  properties: Record<string, unknown>,
): AnalyticsProperties =>
  Object.entries(properties).reduce<AnalyticsProperties>(
    (result, [key, value]) => {
      if (!isSafeAnalyticsPropertyKey(key)) return result;
      if (
        typeof value !== 'boolean' &&
        typeof value !== 'number' &&
        typeof value !== 'string'
      ) {
        return result;
      }

      result[key] = value;
      return result;
    },
    {},
  );
